<!-- 组员选择弹窗 -->
<template>
  <dialog-reset
    :visible="visible"
    title="选择组员"
    width="70%"
    height="60vh"
    top="10vh"
    zoom-show
    :show-close="true"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="member-select-wrap">
      <div class="member-select-container">
        <avue-crud
          ref="crud"
          :option="option"
          :table-loading="loading"
          :data="data"
          :page.sync="page"
          :search.sync="query"
          @search-change="searchChange"
          @search-reset="searchReset"
          @refresh-change="refreshChange"
          @current-change="currentChange"
          @size-change="sizeChange"
          @on-load="onLoad"
          @selection-change="handleSelectionChange"
          @select="handleTableSelect"
          @select-all="handleTableSelectAll"
        />
      </div>
    </div>

    <template slot="dialog_footer">
      <div class="dialog-footer-unified">
        <div class="dialog-footer-unified__chips">
          <span v-if="selectionList.length" class="dialog-footer-unified__label">将确认：</span>
          <el-tag
            v-for="member in selectionList"
            :key="'ft-' + member.id"
            closable
            size="small"
            class="dialog-footer-unified__tag"
            @close="removeMember(member)"
          >
            {{ member.realName }}
          </el-tag>
          <span v-if="selectionList.length === 0" class="dialog-footer-unified__empty">请选择组员</span>
        </div>
        <div class="dialog-footer-unified__actions">
          <el-button size="small" @click="handleClose">取消</el-button>
          <el-button
            type="primary"
            size="small"
            :disabled="selectionList.length === 0"
            @click="handleConfirm"
          >
            确认选择
          </el-button>
        </div>
      </div>
    </template>
  </dialog-reset>
</template>

<script>
import crudMixins from '@/mixins/crud-mixins'
import DialogReset from '@/basic-common/components/DialogReset.vue'
import { getMemberList } from '../api'

export default {
  name: 'MemberSelectDialog',
  components: {
    DialogReset,
  },
  mixins: [crudMixins],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    currentRow: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      apiConfig: { getList: getMemberList },
      selectionList: [],
      /** 程序化勾选表格时忽略 selection-change，避免清空已解析的 selectionList */
      _applyingPresetSelection: false,
      /**
       * 表格数据刷新后会先发出「空选区」的 selection-change；此期间勿用空数组覆盖已从表单带入的 selectionList
       */
      _presetSyncPending: false,
      /** 当前页上一次表格勾选快照，用于识别用户取消勾选 */
      _lastTableSelection: [],
      _syncTableTimer: null,
      option: {
        menu: false,
        addBtn: false,
        delBtn: false,
        editBtn: false,
        viewBtn: false,
        tip: false,
        searchMenuSpan: 6,
        selection: true,
        rowKey: 'id',
        reserveSelection: true,
        column: [
          {
            label: '姓名',
            prop: 'realName',
            overHidden: true,
            search: true,
            searchPlaceholder: '请输入姓名',
            searchSpan: 8,
          },
          {
            label: '手机号',
            prop: 'phone',
            overHidden: true,
            search: true,
            searchPlaceholder: '请输入手机号',
            searchSpan: 8,
          },
          {
            label: '单位名称',
            prop: 'deptName',
            overHidden: true,
            search: true,
            searchPlaceholder: '请输入单位名称',
            searchSpan: 8,
          },
          {
            label: '单位ID',
            prop: 'deptId',
            overHidden: true,
            search: false,
            hide: true,
          },
        ],
      },
    }
  },

  watch: {
    visible(val) {
      if (val) {
        this._lastTableSelection = []
        this.parseExistingMembers()
        this.page.currentPage = 1
        this.onLoad(this.page)
      } else {
        this.selectionList = []
        this._presetSyncPending = false
        this._lastTableSelection = []
      }
    },
    selectionList: {
      handler() {
        if (this.visible && !this._applyingPresetSelection) {
          this.scheduleSyncTableSelection()
        }
      },
      deep: true,
    },
  },

  methods: {
    /**
     * 解析当前表单已选组员，供编辑时回显
     */
    parseExistingMembers() {
      const row = this.currentRow
      if (!row) {
        this.selectionList = []
        return
      }
      if (row.memberList && row.memberList.length) {
        this.selectionList = row.memberList.map((m) => ({
          id: m.teamMemberId || m.id,
          realName: m.teamMemberName || m.realName,
          phone: m.phone,
          deptName: m.deptName,
          deptId: m.deptId,
        }))
        return
      }
      const rawNames = row.memberNames || row.members
      if (!rawNames || !String(rawNames).trim()) {
        this.selectionList = []
        return
      }
      const ids = (row.memberIds || '').split(',').map((s) => s.trim())
      const names = String(rawNames)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      this.selectionList = names.map((name, i) => ({
        id: ids[i] || `existing_${name}`,
        realName: name,
        phone: '',
        deptName: '',
        deptId: null,
      }))
    },

    /**
     * 覆盖 mixin：搜索时保留已选组员
     */
    searchChange(params, done) {
      this.query = params
      this.page.currentPage = 1
      this.reloadMemberTable(true)
      this.onLoad(this.page, params)
      done()
    },

    /**
     * 覆盖 mixin：重置筛选时保留已选组员
     */
    searchReset() {
      this.query = {}
      this.initSearchValue()
      this.page.currentPage = 1
      this.reloadMemberTable(true)
      this.onLoad(this.page, this.query)
    },

    /**
     * 刷新表格勾选状态；可选保留 selectionList（查询/翻页时不丢已选组员）
     */
    reloadMemberTable(preserveSelection) {
      if (preserveSelection && this.selectionList && this.selectionList.length > 0) {
        this._presetSyncPending = true
      }
      this.$nextTick(() => {
        this.clearTableCheckboxOnly()
      })
    },

    handleSelectionChange(selection) {
      if (this._applyingPresetSelection) {
        return
      }
      if (
        this._presetSyncPending &&
        selection.length === 0 &&
        this.selectionList.length > 0
      ) {
        return
      }
      this.mergeTableSelectionAddOnly(selection)
    },

    /**
     * 覆盖 crudMixins.selectionChange，避免被 Avue 内部直接赋值为「当前页勾选」
     */
    selectionChange(selection) {
      this.handleSelectionChange(selection)
    },

    /**
     * 单行勾选/取消：取消时从已选列表移除，勾选时追加
     */
    handleTableSelect(selection, row) {
      if (this._applyingPresetSelection || !row) {
        return
      }
      const stillSelected = selection.some((item) => this.rowMatchesMember(item, row))
      if (!stillSelected) {
        this.selectionList = this.selectionList.filter(
          (sel) => !this.rowMatchesMember(row, sel)
        )
      } else if (!this.selectionList.some((sel) => this.rowMatchesMember(row, sel))) {
        this.selectionList = [...this.selectionList, row]
      }
      this._lastTableSelection = selection
    },

    /**
     * 当前页全选/全不选
     */
    handleTableSelectAll(selection) {
      if (this._applyingPresetSelection) {
        return
      }
      const currentPageRows = this.data || []
      if (!selection.length) {
        this.selectionList = this.selectionList.filter(
          (sel) => !currentPageRows.some((row) => this.rowMatchesMember(row, sel))
        )
      } else {
        this.mergeTableSelectionAddOnly(selection)
      }
      this._lastTableSelection = selection
    },

    /**
     * 仅追加表格新勾选项，不因表格未回显勾选而误删已有组员
     */
    mergeTableSelectionAddOnly(selection) {
      const merged = [...this.selectionList]
      let changed = false
      selection.forEach((row) => {
        if (!merged.some((m) => this.rowMatchesMember(row, m))) {
          merged.push(row)
          changed = true
        }
      })
      if (changed) {
        this.selectionList = merged
      }
      this._lastTableSelection = selection
    },

    /**
     * 列表加载完成后，将已选组员与当前页表格行对齐并勾选复选框
     */
    afterSearch() {
      if (this.selectionList && this.selectionList.length > 0) {
        this._presetSyncPending = true
      }
      this.scheduleSyncTableSelection()
    },

    /**
     * 延迟合并多次同步请求，避免列表/勾选互相触发
     */
    scheduleSyncTableSelection() {
      if (this._syncTableTimer) {
        clearTimeout(this._syncTableTimer)
      }
      this._syncTableTimer = setTimeout(() => {
        this._syncTableTimer = null
        this.syncTableSelectionFromList()
      }, 0)
    },

    /**
     * 根据 selectionList 回显当前页表格勾选（含编辑带入、翻页后仍在已选列表中的行）
     */
    syncTableSelectionFromList() {
      const crud = this.$refs.crud
      if (!crud || !this.data || !this.data.length) {
        this._presetSyncPending = false
        return
      }
      if (!this.selectionList || !this.selectionList.length) {
        this.clearTableCheckboxOnly()
        this._presetSyncPending = false
        return
      }

      const checkedRows = this.data.filter((row) =>
        this.selectionList.some((sel) => this.rowMatchesMember(row, sel))
      )

      this._applyingPresetSelection = true
      const apply = () => {
        if (typeof crud.toggleSelection === 'function') {
          crud.toggleSelection()
          if (checkedRows.length) {
            crud.toggleSelection(checkedRows)
          }
        } else {
          const table = crud.$refs && crud.$refs.table
          if (table && table.clearSelection) {
            table.clearSelection()
            checkedRows.forEach((row) => {
              table.toggleRowSelection(row, true)
            })
          }
        }
        this.selectionList = this.selectionList.map((sel) => {
          const row = this.data.find((r) => this.rowMatchesMember(r, sel))
          return row || sel
        })
        this._lastTableSelection = checkedRows
        this._applyingPresetSelection = false
        this._presetSyncPending = false
      }

      this.$nextTick(() => {
        apply()
        this.$nextTick(apply)
      })
    },

    /**
     * 仅清除表格勾选，不清空 selectionList
     */
    clearTableCheckboxOnly() {
      const crud = this.$refs.crud
      if (crud && typeof crud.toggleSelection === 'function') {
        crud.toggleSelection()
        return
      }
      const table = crud && crud.$refs && crud.$refs.table
      if (table && table.clearSelection) {
        table.clearSelection()
      }
    },

    /**
     * 刷新列表时保留已选组员（混入默认会 selectionClear 导致丢失）
     */
    refreshChange() {
      if (this.selectionList && this.selectionList.length > 0) {
        this._presetSyncPending = true
      }
      this.onLoad(this.page, this.query)
    },

    /**
     * 行数据与已选组员是否同一用户（优先 id，其次姓名）
     */
    rowMatchesMember(row, sel) {
      if (!row || !sel) {
        return false
      }
      const rowIds = [row.id, row.userId, row.teamMemberId]
        .filter((v) => v != null && v !== '')
        .map((v) => String(v))
      const selIds = [sel.id, sel.userId, sel.teamMemberId]
        .filter((v) => v != null && v !== '' && !String(v).startsWith('existing_'))
        .map((v) => String(v))
      if (rowIds.length && selIds.length && rowIds.some((rid) => selIds.includes(rid))) {
        return true
      }
      const rname = (row.realName || row.teamMemberName || '').trim()
      const sname = (sel.realName || sel.teamMemberName || '').trim()
      return Boolean(rname && sname && rname === sname)
    },

    /**
     * 清空表格勾选与已选列表
     */
    clearTableSelection() {
      this.selectionList = []
      this._lastTableSelection = []
      this.$nextTick(() => {
        this.clearTableCheckboxOnly()
      })
    },

    /**
     * 移除一名已选组员（与表格勾选同步）
     */
    removeMember(member) {
      const index = this.selectionList.findIndex((m) => this.rowMatchesMember(m, member))
      if (index > -1) {
        this.selectionList.splice(index, 1)
      }
      const row = this.data.find((d) => this.rowMatchesMember(d, member))
      if (row && this.$refs.crud && this.$refs.crud.$refs.table) {
        this._applyingPresetSelection = true
        this.$refs.crud.$refs.table.toggleRowSelection(row, false)
        this._lastTableSelection = this._lastTableSelection.filter(
          (item) => !this.rowMatchesMember(item, row)
        )
        this.$nextTick(() => {
          this._applyingPresetSelection = false
        })
      }
    },

    /**
     * 确认选择
     */
    handleConfirm() {
      if (this.selectionList.length === 0) {
        this.$message.warning('请选择至少一名组员')
        return
      }
      const memberNames = this.selectionList.map((member) => member.realName).join(',')
      const memberIds = this.selectionList.map((member) => member.id).join(',')
      this.$emit('confirm', {
        memberIds,
        memberNames,
        memberList: this.selectionList,
        currentRow: this.currentRow,
      })
      this.$emit('update:visible', false)
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      if (this._syncTableTimer) {
        clearTimeout(this._syncTableTimer)
        this._syncTableTimer = null
      }
      this.$emit('update:visible', false)
      this.selectionList = []
    },
  },
}
</script>

<style lang="scss" scoped>
.member-select-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.member-select-container {
  ::v-deep .avue-crud__menu {
    display: none !important;
  }
  ::v-deep .avue-crud__tip {
    display: none !important;
  }
}

.dialog-footer-unified {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
}

.dialog-footer-unified__chips {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #606266;
}

.dialog-footer-unified__label {
  color: #909399;
  margin-right: 4px;
}

.dialog-footer-unified__tag {
  margin-right: 0;
}

.dialog-footer-unified__empty {
  color: #909399;
  font-size: 13px;
}

.dialog-footer-unified__actions {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;

  ::v-deep .el-button {
    min-width: 96px;
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>
